import React, { useState } from 'react';
import { Box, Button, Stack, Typography, Paper, IconButton, CircularProgress, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import EducationFormDialog from 'features/feature_cv/components/dialogs/EducationFormDialog';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch {
        return dateString;
    }
};

function EducationSection({ cvId }) {
    const queryClient = useQueryClient();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingEducation, setEditingEducation] = useState(null);

    const { data, isLoading, refetch } = useQuery(
        ['cv_education', cvId],
        async () => {
            const service = new HttpService();
            const response = await service.get(`/job/cv/education/?cv=${cvId}`, null);
            return Array.isArray(response) ? response : (response?.results || []);
        },
        { enabled: !!cvId }
    );

    const deleteMutation = useMutation(
        (id) => CVHttpService.deleteEducation(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['cv_education', cvId]);
            }
        }
    );

    const educations = data || [];

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this education entry?')) return;
        deleteMutation.mutate(id);
    };

    const handleSave = async () => {
        await refetch();
        setOpenDialog(false);
        setEditingEducation(null);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                    Education ({educations.length})
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingEducation(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Education
                </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {educations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No education entries added yet.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {educations.map((edu) => (
                        <Paper key={edu.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {formatText(edu.level)}
                                    </Typography>
                                    {edu.field && (
                                        <Typography variant="body2" color="text.secondary">
                                            {formatText(edu.field)}
                                        </Typography>
                                    )}
                                    {edu.location && (
                                        <Typography variant="body2" color="text.secondary">
                                            {formatText(edu.location)}
                                        </Typography>
                                    )}
                                    {(edu.start_date || edu.end_date) && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {formatDate(edu.start_date)} - {edu.end_date ? formatDate(edu.end_date) : 'Present'}
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                    <IconButton color="primary"
                                        size="small"
                                        onClick={() => {
                                            setEditingEducation(edu);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(edu.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <EducationFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingEducation(null);
                }}
                onSave={handleSave}
                cvId={cvId}
                education={editingEducation}
            />
        </Paper>
    );
}

export default EducationSection;

